import React, {useCallback, useEffect, useState} from 'react';
import {Area, Legend, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import moment from 'moment';
import {ChartSkeleton} from '../../../../shared/ui/LoadingSkeletons';
import {fetchTimeSeriesData} from '../../services/influxApi';
import {useStatistics} from '../../context/StatisticsContext';
import '../../styles/statistics.css';

const colors = ["#4590D1", "#BF2C2C", "#F4AE00"];

const TimeSeriesChart = ({chart}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [dataKeys, setDataKeys] = useState([]);
  const [zoomState, setZoomState] = useState({
    refAreaLeft: '',
    refAreaRight: '',
    left: 'dataMin',
    right: 'dataMax',
    top: 'dataMax+1',
    bottom: 'dataMin',
  });

  const {start, end, updateAbsoluteRange, edgeId, chartsConfig} = useStatistics();

  const getData = useCallback(async () => {
    if (!edgeId || !start || !end || !chart) return;

    setLoading(true);
    const nrOfDataPoints = chartsConfig?.nrOfDataPoints || 360;
    const result = await fetchTimeSeriesData(edgeId, chart, nrOfDataPoints, start, end);

    if (!result.ok) {
      console.error('Error fetching chart data:', result.error);
      setData([]);
      setDataKeys([]);
    } else {
      setData(result.data.data || []);
      setDataKeys(result.data.dataKeys || []);
    }
    setLoading(false);
  }, [edgeId, chart, start, end, chartsConfig]);

  useEffect(() => {
    getData();
  }, [start, end, chart, edgeId, getData]);

  const zoom = () => {
    if (zoomState.refAreaLeft === zoomState.refAreaRight || zoomState.refAreaRight === '') {
      setZoomState({...zoomState, refAreaLeft: '', refAreaRight: ''});
      return;
    }

    if (zoomState.refAreaLeft > zoomState.refAreaRight) {
      [zoomState.refAreaLeft, zoomState.refAreaRight] = [zoomState.refAreaRight, zoomState.refAreaLeft];
    }

    setZoomState({
      ...zoomState,
      left: zoomState.refAreaLeft,
      right: zoomState.refAreaRight,
      refAreaLeft: '',
      refAreaRight: '',
    });

    updateAbsoluteRange(zoomState.refAreaLeft, zoomState.refAreaRight);
  };

  const clearTextSelection = () => {
    const selection = globalThis.getSelection?.();
    selection?.removeAllRanges?.();
  };

  const CustomTooltip = ({active, payload, label}) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`${moment(label).format('DD.MM HH:mm')}`}</p>
          {
            payload.map((item, index) => (
              <p key={index} style={{color: item.color}}>{`${item.name}: ${item.value}`}</p>
            ))
          }
        </div>
      );
    }
    return null;
  };

  return (
    <div className="lchart">
      <p>{chart.name}</p>
      {
        loading ? (
          <ChartSkeleton height={275}/>
        ) : (data && dataKeys ? (
          <ResponsiveContainer width="100%" height={275}>
            <LineChart
              data={data}
              onMouseDown={(e) => {
                clearTextSelection();
                if (!e?.activeLabel) return;
                setZoomState({...zoomState, refAreaLeft: e.activeLabel});
              }}
              onMouseMove={(e) => zoomState.refAreaLeft && setZoomState({...zoomState, refAreaRight: e.activeLabel})}
              onMouseUp={zoom}
              margin={{
                top: 0, right: 0, left: 0, bottom: 0,
              }}
            >
              <XAxis
                dataKey="_time"
                allowDataOverflow
                domain={[zoomState.left, zoomState.right]}
                tickFormatter={(str) => moment(str).format('DD.MM HH:mm')}
                axisLine={false}
                padding={{left: 10}}
              />
              <YAxis
                domain={['auto', 'auto']}
                axisLine={false}
                padding={{bottom: 10}}
              />
              <Legend className="legend" verticalAlign="top" align="right"/>
              <Tooltip content={<CustomTooltip/>}/>
              {dataKeys.map((k, index) => (
                <React.Fragment key={k}>
                  <Line type="monotone" dataKey={k} stroke={colors[index % colors.length]} dot={false}/>
                  <Area type="monotone" dataKey={k} fill={colors[index % colors.length]} fillOpacity={1}/>
                </React.Fragment>
              ))}
              {(zoomState.refAreaLeft && zoomState.refAreaRight) ? (
                <ReferenceArea
                  x1={zoomState.refAreaLeft}
                  x2={zoomState.refAreaRight}
                  strokeOpacity={0.3}
                />
              ) : null}
            </LineChart>
          </ResponsiveContainer>
        ) : <ChartSkeleton height={275}/>)
      }
    </div>
  );
};

export default TimeSeriesChart;
